import React from 'react';
import { Tooltip } from 'antd';
import { formatNumber } from '../../../utils/numberFormat';

/** The holding fields this file reads. t*buyqty are set only by brokers that report settlement (Pinetree). */
export interface SettlementHolding {
  symbol: string;
  quantity: number;
  availableqty: number;
  deliverableqty: number;
  stockdividend: number;
  capital: number;
  t0buyqty?: number;
  t1buyqty?: number;
  t2buyqty?: number;
}

/**
 * A row of a strategy's Holdings card: quantity and capital are the strategy's own; the other
 * figures are the broker's, so on a symbol `sharedWith` another strategy they cover both.
 */
export interface HoldingRowData extends SettlementHolding {
  sharedWith: string[];
  /** The broker's whole-account quantity; null when it reports none. */
  accountQuantity: number | null;
}

type SettlementQty = Pick<SettlementHolding, 't0buyqty' | 't1buyqty' | 't2buyqty'>;

const STRATEGY_LABEL: Record<string, string> = { GRID: 'grid', PAIR: 'pair' };

/** "Shared" beside a symbol another strategy also holds, with the account total in the tooltip. */
export const SharedBadge: React.FC<{ holding: Pick<HoldingRowData, 'sharedWith' | 'accountQuantity'> }> = ({ holding }) => {
  if (holding.sharedWith.length === 0) return null;
  const others = holding.sharedWith.map((t) => STRATEGY_LABEL[t] ?? t.toLowerCase()).join(', ');
  return (
    <Tooltip
      title={
        `Also held by ${others} on this account. Quantity and Capital are this strategy's own; ` +
        `the other figures are the whole account's` +
        (holding.accountQuantity != null ? ` (account holds ${formatNumber(holding.accountQuantity)}).` : '.')
      }
    >
      <span style={{ fontSize: 10, fontWeight: 600, padding: '0 5px', borderRadius: 3, border: '1px solid #3C434D', color: '#B7BDC6', cursor: 'help' }}>
        Shared
      </span>
    </Tooltip>
  );
};

/** In arrival order (T+2.5): a T2 buy lands at 12:00 VN today, T1 next session, T0 the one after. */
const BUCKETS = [
  { key: 't2buyqty', label: 'T2', when: 'today 12:00', hint: 'Arrives today at 12:00', color: '#F0B90B' },
  { key: 't1buyqty', label: 'T1', when: 'next session', hint: 'Arrives next session at 12:00', color: '#4C9AFF' },
  { key: 't0buyqty', label: 'T0', when: 'in 2 sessions', hint: 'Arrives in 2 sessions at 12:00', color: '#A78BFA' },
] as const;

const ARRIVED = '#0ECB81';
const MUTED = '#5E6673';

const incomingOf = (h: SettlementQty) => BUCKETS.map((b) => ({ ...b, qty: h[b.key] ?? 0 })).filter((b) => b.qty > 0);

/** Only Pinetree reports settlement; other brokers leave every bucket unset. */
export const reportsSettlement = (rows: SettlementQty[]) =>
  rows.some((h) => h.t0buyqty != null || h.t1buyqty != null || h.t2buyqty != null);

/** "T2 300 · T1 200 · T0 100" — shares bought and still on their way, soonest first. */
export const IncomingQty: React.FC<{ holding: SettlementQty }> = ({ holding }) => {
  const parts = incomingOf(holding);
  if (parts.length === 0) return <span style={{ color: MUTED }}>—</span>;
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {parts.map((b, i) => (
        <Tooltip key={b.key} title={b.hint}>
          {i > 0 && <span style={{ color: MUTED }}> · </span>}
          <span style={{ color: b.color, fontWeight: 600 }}>{b.label}</span>{' '}
          <span style={{ color: '#EAECEF' }}>{formatNumber(b.qty)}</span>
        </Tooltip>
      ))}
    </span>
  );
};

const Dot: React.FC<{ color: string }> = ({ color }) => (
  <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: color, marginRight: 5, verticalAlign: 1 }} />
);

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: 11, color: '#848E9C' }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 600, color: '#EAECEF', whiteSpace: 'nowrap' }}>{value}</div>
  </div>
);

const HoldingRow: React.FC<{ h: SettlementHolding | HoldingRowData; settlement: boolean; first: boolean }> = ({ h, settlement, first }) => {
  const incoming = incomingOf(h);
  const pending = incoming.reduce((s, b) => s + b.qty, 0);
  const total = Math.max(h.quantity, 1);
  const arrived = Math.max(0, h.quantity - pending);

  return (
    <div style={{ padding: '12px 0', borderTop: first ? 'none' : '1px solid #2B3139' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#EAECEF' }}>{h.symbol}</span>
          {'sharedWith' in h && <SharedBadge holding={h} />}
        </span>
        <span style={{ fontSize: 15, fontWeight: 700, color: h.quantity >= 0 ? '#0ECB81' : '#F6465D' }}>
          {formatNumber(h.quantity)}
        </span>
      </div>

      {settlement && h.quantity > 0 && (
        <>
          <div
            role="img"
            aria-label={`${h.symbol}: ${arrived} arrived, ${pending} incoming`}
            style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: '#2B3139', margin: '8px 0 6px' }}
          >
            <div style={{ width: `${(arrived / total) * 100}%`, background: ARRIVED }} />
            {incoming.map((b) => (
              <div key={b.key} style={{ width: `${(b.qty / total) * 100}%`, background: b.color }} />
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: 12, color: '#B7BDC6' }}>
            <span>
              <Dot color={ARRIVED} />Arrived <b style={{ color: '#EAECEF' }}>{formatNumber(arrived)}</b>
            </span>
            {incoming.map((b) => (
              <span key={b.key}>
                <Dot color={b.color} />{b.label} <b style={{ color: '#EAECEF' }}>{formatNumber(b.qty)}</b>
                <span style={{ color: MUTED }}> · {b.when}</span>
              </span>
            ))}
          </div>
        </>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, marginTop: 10 }}>
        <Stat label="Available" value={formatNumber(h.availableqty ?? 0)} />
        <Stat label="Deliverable" value={formatNumber(h.deliverableqty ?? 0)} />
        <Stat label="Capital" value={formatNumber(Math.round(h.capital ?? 0))} />
      </div>
      {(h.stockdividend ?? 0) > 0 && (
        <div style={{ fontSize: 12, color: '#B7BDC6', marginTop: 6 }}>
          Stock dividend <b style={{ color: '#EAECEF' }}>{formatNumber(h.stockdividend)}</b>
        </div>
      )}
    </div>
  );
};

/**
 * Holdings on a phone: one block per symbol instead of a seven-column table to scroll
 * sideways. Where the broker reports settlement, a bar shows what has arrived and what is
 * still on its way.
 */
export const MobileHoldingsList: React.FC<{ holdings: (SettlementHolding | HoldingRowData)[] }> = ({ holdings }) => {
  const settlement = reportsSettlement(holdings);
  return (
    <div>
      {holdings.map((h, i) => (
        <HoldingRow key={`${h.symbol}-${i}`} h={h} settlement={settlement} first={i === 0} />
      ))}
    </div>
  );
};
